import { useEffect, useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import content from "../assets/content-1.png";
import content3 from "../assets/content-3.png";
import content2 from "../assets/content-2.png";
import content4 from "../assets/content-4.png";
import heroPrimary from "../assets/content-hero.png";

const featureHighlights = [
  {
    title: "Video Pembelajaran oleh Mentor",
    description: "Akses video pembelajaran dari para ahli yang telah membimbing ribuan trader sukses.",
  },
  {
    title: "Komunitas Trading No.1",
    description: "Bergabung dengan komunitas terdepan yang selalu aktif dengan pembelajaran live setiap minggunya.",
  },
  {
    title: "Signal Akurat Tinggi",
    description: "Dapatkan analisa harian dengan akurasi mencapai 90% yang telah terbukti efektif di pasar.",
  },
  {
    title: "Live Trading Eksklusif",
    description: "Akses ruang trading eksklusif dengan strategi real-time langsung dari para mentor profesional.",
  },
];

const neonStats = [
  { target: 2000, suffix: "+", label: "Member Joined", description: "Ribuan member sudah bergabung." },
  { target: 10000, prefix: "$", suffix: "+", label: "Profit Members", description: "Total profit komunitas setiap bulan." },
  { target: 25, suffix: "+", label: "Trade Hours", description: "Total jam trading dalam setiap minggu" },
];

const AnimatedCounter = ({ target, prefix = "", suffix = "", duration = 3000 }) => {
  const [value, setValue] = useState(0);

  useEffect(() => {
    let frameId;
    const startTime = performance.now();

    const animate = (currentTime) => {
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setValue(Math.floor(progress * target));
      if (progress < 1) {
        frameId = requestAnimationFrame(animate);
      }
    };

    frameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameId);
  }, [target, duration]);

  return `${prefix}${value.toLocaleString("id-ID")}${suffix}`;
};

const reasons = [
  {
    title: "Menghadirkan Sinyal Trading Real-Time",
    description:
      "ArfaFX menyediakan signal akurat setiap harinya yang sangat membantu kamu untuk menganalisis pasar bahkan hemat waktu hingga 80%.",
    image: content,
    pill: "After Analysis",
  },
  {
    title: "Komunitas Eksklusif Khusus Member",
    description:
      "Gabung dengan ratusan high net worth trader yang saling berbagi market potensial dan support satu sama lain lewat grup privat.",
    image: content3,
    pill: "Community Only",
  },
  {
    title: "Berita Terupdate",
    description:
      "Update terbaru berita yang berdampak ke Forex & Commodity",
    image: content2,
    pill: "FXS Feed",
  },
];

const testimonials = [
  {
    name: "Rahmatul Hidayah",
    quote:
      "Semenjak kenal ArfaFX saya jadi mengerti bagaimana cara baca market, money management, dan jadi lebih disiplin setiap hari.",
  },
  {
    name: "Marsella",
    quote:
      "Alhamdulillah, setelah ngerasain MC akhirnya bisa recovery bareng coach. Ilmu baru tiap minggu dan mentor serius jawab semua pertanyaan.",
  },
  {
    name: "Fernando",
    quote:
      "Dulu trading asal-asalan. Di komunitas ini semua dikasih step-by-step, ada zoom rutin dan signal jelas jadi lebih percaya diri.",
  },
];

const membershipBenefits = [
  "Akses ke channel Premium ArfaFX",
  "Materi eksklusif yang bisa diakses selama 24 jam kapanpun",
  "Market outlook & daily trade bersama ahlinya setiap hari",
  "Signal Premium setiap harinya yang sangat membantu untuk menganalisis pasar bahkan hemat waktu hingga 90%.",
];

const AnimatedSection = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.section
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.6, delay }}
    >
      {children}
    </motion.section>
  );
};

const Hero = () => {
  return (
    <main className="relative z-0">
      {/* Hero Section */}
      <motion.section
        className="relative overflow-hidden bg-gradient-to-b from-[#14001f] via-[#07000f] to-[#050007] py-20"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="absolute -left-32 top-10 h-72 w-72 rounded-full bg-[#ff00bf]/15 blur-[160px]" />
        <div className="absolute right-0 bottom-0 h-80 w-80 rounded-full bg-[#541275]/40 blur-[180px]" />

        <div className="relative mx-auto flex max-w-5xl flex-col items-center gap-6 px-6 mt-10 text-center">
          <motion.div
            className="space-y-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h1 className="text-3xl font-black leading-snug text-white sm:text-4xl lg:text-6xl">
              Belajar Trading Forex
              <span className="block font-black bg-gradient-to-r from-[#ff00bf] via-[#ff7aec] to-[#ffd6ff] bg-clip-text text-transparent">
                Dari Profesional Trading Teams
              </span>
            </h1>
          </motion.div>

          <motion.div
            className="w-full"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <div className="relative mx-auto max-w-3xl">
              <div className="relative overflow-hidden rounded-3xl">
                <img src={heroPrimary} alt="ArfaFX Sessions" className="w-full object-cover" />
              </div>
            </div>
          </motion.div>
          <motion.div
            className="flex flex-col items-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="https://t.me/joinpremiumarfafx"
                className="rounded-full bg-[#ff00bf] px-8 py-3 font-semibold text-white shadow-[0_25px_60px_rgba(255,0,191,0.4)] transition hover:scale-[1.1]"
              >
                Gabung Sekarang
              </a>
              <a
                href="https://t.me/arfatradingforex"
                className="rounded-full border border-white/20 px-8 py-3 font-semibold text-white hover:border-white/40 hover:scale-[1.1]"
              >
                Join Free Channel
              </a>
            </div>
            <p className="text-lg text-white/70 max-w-3xl">
              Gabung sekarang agar profit sebanyak mungkin
            </p>
          </motion.div>
        </div>
      </motion.section>

      {/* Neon Section */}
      <AnimatedSection className="bg-gradient-to-r from-[#ff00bf] via-[#ff6bff] to-[#ffc4ff] py-16">
        <div className="mx-auto max-w-6xl px-6 text-black">
          <motion.div
            className="text-center mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm font-semibold uppercase tracking-[0.4em] text-black/50">
              Profit Konsisten dan Signal Akurat
            </p>
            <h2 className="mt-4 text-3xl font-black text-black">
              Bergabunglah Dengan Kami Untuk Profit Maksimal
            </h2>
          </motion.div>

          <div className="grid gap-6 md:grid-cols-3">
            {neonStats.map((stat, index) => (
              <motion.div
                key={stat.label}
                className="rounded-[32px] border border-black/10 bg-gradient-to-br from-[#070009]/95 via-[#0b0014]/90 to-[#330040]/80 p-6 text-white shadow-[0_30px_80px_rgba(50,0,50,0.45)]"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
              >
                <div className="text-4xl font-black text-white">
                  <AnimatedCounter target={stat.target} prefix={stat.prefix} suffix={stat.suffix} />
                </div>
                <p className="mt-2 text-lg font-semibold text-white/90">{stat.label}</p>
                <p className="mt-3 text-sm text-white/70">{stat.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* About Section */}
      <AnimatedSection id="about" className="relative bg-gradient-to-b from-[#0a0014] via-[#050009] to-[#090013] py-10">
        <div className="absolute inset-0 bg-gradient-to-r from-[#ff9dff]/10 to-transparent" />
        <div className="relative mx-auto max-w-6xl grid gap-12 px-6 lg:grid-cols-2">
          <motion.div
            className="rounded-[32px] border border-white/10 bg-white/5 p-8"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="inline-flex items-center rounded-full border border-[#ff7aec]/70 px-4 py-1 text-xs uppercase tracking-[0.3em] text-[#ff7aec]">
              Tentang ArfaFX
            </p>
            <h2 className="mt-6 text-3xl font-bold text-white">
              Apa itu ArfaFX?
            </h2>
            <p className="mt-4 text-white/70">
              ArfaFX merupakan komunitas trading Forex & Commodity terpercaya di Indonesia yang telah membantu ribuan trader profit maksimal.
              Kami menyediakan berbagai fasilitas lengkap dan layanan edukasi premium GRATIS untuk mensupport kesuksesan trading Anda.
            </p>

            <ul className="mt-8 space-y-5 ">
              {featureHighlights.map((feature, index) => (
                <motion.li
                  key={feature.title}
                  className="flex gap-4"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <span className="mt-1 inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#ff00bf]/15 text-[#ff9dff]">
                    ✓
                  </span>
                  <div>
                    <p className="font-semibold text-white">{feature.title}</p>
                    <p className="text-sm text-white/60">{feature.description}</p>
                  </div>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            className="relative"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="rounded-[30px] border border-white/10 bg-[#0b110a] p-6 shadow-[0_40px_120px_rgba(0,0,0,0.6)]">
              <img src={content4} alt="Dashboard" className="w-full rounded-2xl border border-white/10" />
              <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 px-6 py-5">
                <p className="text-sm text-white/60">Total profit seminggu berdasaran Price Interest Point (PIP)</p>
                <div className="mt-3 flex items-center justify-between">
                  <p className="text-4xl font-black text-white">6000+ PIPS</p>
                  <a href="https://t.me/joinpremiumarfafx" className="rounded-full bg-[#ff00bf] px-6 py-2 text-sm font-semibold text-white shadow-[0_15px_40px_rgba(255,0,191,0.35)]">
                    Gabung Sekarang
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </AnimatedSection>


      {/* Why Choose Section */}
      <AnimatedSection className="py-10">
        <motion.div
          className="mx-auto max-w-5xl px-6 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="inline-flex items-center rounded-full border border-[#ff7aec] px-5 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-[#ff7aec]">
            Kenapa Harus Memilih ArfaFX
          </p>
          <h2 className="mt-6 text-3xl font-black text-white sm:text-4xl">
            90% Traders membuktikan kami membantu mereka dalam Trading Forex!
          </h2>
        </motion.div>

        <div className="mx-auto mt-16 grid max-w-6xl gap-10 px-6">
          {reasons.map((item, idx) => (
            <motion.div
              key={item.title}
              className="grid gap-8 rounded-[30px] border border-[#ff7aec]/30 bg-[#120019]/80 p-6 lg:grid-cols-2"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: idx * 0.2 }}
              whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
            >
              <div className={`${idx % 2 === 0 ? "order-1" : "order-2"} text-left`}>
                <p className="text-sm uppercase tracking-[0.3em] text-[#ff8bff]">0{idx + 1}</p>
                <h3 className="mt-3 text-3xl font-black bg-gradient-to-r from-[#ff00bf] via-[#ff7aec] to-[#ffd6ff] bg-clip-text text-transparent">
                  {item.title}
                </h3>
                <p className="mt-4 text-white/70">{item.description}</p>
              </div>
              <div className={`${idx % 2 === 0 ? "order-2" : "order-1"} relative`}>
                <div className="relative rounded-[26px] border border-white/10 bg-[#050505] p-4">
                  <img src={item.image} alt={item.title} className="w-full h-auto max-h-96 object-contain" />
                  <span className="absolute top-4 right-4 rounded-full bg-[#ff00bf] px-4 py-1 text-xs font-semibold text-white">
                    {item.pill}
                  </span>
                </div>
                {idx < reasons.length - 1 && (
                  <div className="hidden lg:block absolute left-1/2 mt-4 h-20 w-px -translate-x-1/2 border-2 border-dashed border-white/50" />
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </AnimatedSection>


      {/* Membership */}
      <AnimatedSection className="bg-gradient-to-b from-[#25002f] via-[#110019] to-[#040005] py-20 px-6">
        <motion.div
          className="mx-auto max-w-4xl rounded-[32px] border border-[#ff7aec]/30 bg-[#0c0014]/80 px-8 py-12 text-white shadow-[0_35px_70px_rgba(0,0,0,0.6)]"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            className="flex flex-col items-center text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="rounded-full border border-[#ff7aec]/40 px-5 py-1 text-xs uppercase tracking-[0.3em] text-[#ff9dff]">
              Premium ArfaFX
            </p>
            <h2 className="mt-4 text-3xl font-bold">Join dan Daftar Sekarang!</h2>
            <p className="mt-2 text-white/60">Dapatkan semua akses eksklusif selama 1 tahun penuh</p>
          </motion.div>

          <div className="mt-10 space-y-4 text-sm">
            {membershipBenefits.map((benefit, index) => (
              <motion.div
                key={benefit}
                className="flex items-start gap-3 rounded-2xl border border-white/10 px-4 py-3"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <span className="mt-1 text-[#ff8bff]">✔</span>
                <p className="text-white/80">{benefit}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            className="mt-10 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <p className="mt-2 text-4xl font-black text-white">GRATIS!!</p>
            <a
              href="https://t.me/joinpremiumarfafx"
              className="mt-6 inline-flex items-center justify-center rounded-full bg-[#ff00bf] px-10 py-3 font-semibold text-white shadow-[0_20px_50px_rgba(255,0,191,0.45)] hover:scale-[1.1] transition-transform"
              target="_blank"
              rel="noopener noreferrer"
            >
              Daftar Sekarang
            </a>
          </motion.div>
        </motion.div>
      </AnimatedSection>


      {/* Final CTA */}
      <AnimatedSection className="bg-gradient-to-r from-[#ff00bf] via-[#ff6bff] to-[#ffc4ff] py-16 text-black">
        <div className="mx-auto flex max-w-6xl flex-col gap-8 px-6 lg:flex-row lg:items-center">
          <motion.div
            className="flex-1"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="inline-flex rounded-full border border-black/50 px-4 py-1 text-xs uppercase tracking-[0.3em] text-black">
              Dapatkan semua akses
            </p>
            <h2 className="mt-4 text-3xl font-black">
              Jadilah Trader Profesional dan Raihlah Profit Maksimal!
            </h2>
            <p className="mt-4 text-black/70">
              Ribuan member sudah berhasil profit maksimal dan konsisten dengan strategi dari
              ArfaFX. Saatnya giliran kamu!!
            </p>
            <div className="mt-6 flex flex-wrap gap-4">
              <a
                href="https://t.me/joinpremiumarfafx"
                className="rounded-full bg-black px-8 py-3 text-sm font-semibold text-white shadow-[0_15px_40px_rgba(0,0,0,0.3)] hover:scale-[1.1] transition-transform hover:bg-black hover:text-white"
              >
                Gabung Sekarang
              </a>
              <a
                href="https://t.me/arfatradingforex"
                className="rounded-full border border-black px-8 py-3 text-sm font-semibold text-black hover:scale-[1.1] transition-transform hover:bg-black hover:text-white"
              >
                Join Free Channel
              </a>
            </div>
          </motion.div>
          <motion.div
            className="flex-1"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="">
              <img src={heroPrimary} alt="Komunitas ArfaFX" className="" />
            </div>
          </motion.div>
        </div>
      </AnimatedSection>
    </main>
  );
};

export default Hero;
